import { Navigate } from 'react-router-dom';
import { useAuth } from '@verlo/shared/contexts/AuthContext';

/** Read the cached user from localStorage — used as a fallback when React state
 * has not yet flushed after a fresh login (avoids a race condition where the
 * route guard sees `null` and bounces the user back to landing). */
const cachedUser = () => {
  try { return JSON.parse(localStorage.getItem('verlo_user') || 'null'); }
  catch { return null; }
};

const userHasPerm = (u, perm) => {
  if (!u) return false;
  if (u.rbac_role === 'owner') return true;
  if (Array.isArray(u.effective_permissions)) return u.effective_permissions.includes(perm);
  return u.role === 'admin';
};

/**
 * Route guard — renders children only when the current user has the given permission.
 * Redirects to the user's default landing path on denial (so unauthorised URL access
 * does not produce a blank page).
 *
 *   <RequirePermission perm="reports.view_exec">
 *     <ExecutiveDashboardPage />
 *   </RequirePermission>
 */
const RequirePermission = ({ perm, children, fallback = null }) => {
  const { user, loading } = useAuth();
  if (loading) return null;
  const effective = user || cachedUser();
  if (!effective) return <Navigate to="/login" replace />;
  if (!userHasPerm(effective, perm)) {
    if (fallback) return fallback;
    const home = effective.default_landing_path || '/back-office';
    if (window.location.pathname === home) return null;
    return <Navigate to={home} replace />;
  }
  return children;
};

export default RequirePermission;
